import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';

export default function ProduitAngeReveDoux() {
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const productId = 'ange-reve-doux';
  const defaultImage = 'https://public.readdy.ai/ai/img_res/5d6e1851-3bb6-4061-970b-fd2625a28c0d.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  const product = {
    id: '4',
    name: 'Ange "Rêve Doux"',
    price: 4,
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/5c360c29bbfc3df0ddfc9f600f8c2d5f.png',
    description: 'Le petit ange endormi, plein de tendresse 🌙\nAvec ses joues rosées et ses yeux fermés, cet ange semble rêver de lumière.\nRéalisé à la main à partir de pages de livres anciens, il apporte douceur et sérénité à toute décoration de Noël.\nParfait à suspendre au sapin, offrir ou simplement garder près de soi 💕',
    details: {
      fabrication: 'Fait main dans l\'atelier Les Carnets de Lolotte',
      type: 'Pièce artisanale unique',
      dimensions: '8 cm (hors ficelle)',
      delai: '2 à 3 jours',
      materials: 'Pages de livre recyclées, ficelle naturelle'
    }
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      description: 'Fait main à partir de livres anciens',
      link: '/produit-ange-reve-doux',
    }, quantity);
    
    // Feedback visuel
    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      const originalText = button.innerHTML;
      button.innerHTML = '<i class="ri-check-line mr-2"></i>Ajouté au panier !';
      button.disabled = true;
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/supabase/functions/create-payment-vitrine', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productName: product.name,
          amount: product.price * quantity * 100, // Stripe utilise les centimes
          quantity: quantity,
          currency: 'eur',
          description: `${product.name} - Quantité: ${quantity}`,
          metadata: {
            product_type: 'ange_reve_doux',
            quantity: quantity.toString(),
            fabrication_delay: '2-3 jours'
          }
        }),
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <section className="py-6 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <Link to="/" className="text-[#C49B86] hover:text-[#B97A72] transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <Link to="/noel" className="text-[#C49B86] hover:text-[#B97A72] transition-colors">Collection de Noël</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <span className="text-[#6B5E5A]">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Fiche produit */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4">
                <img
                  src={productImage}
                  alt="Ange Rêve Doux"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Bouton pour modifier l'image */}
              <div className="text-center">
                <button
                  onClick={() => setShowImageUploader(!showImageUploader)}
                  className="bg-[#B97A72] text-white px-6 py-3 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-image-edit-line mr-2"></i>
                  {showImageUploader ? 'Fermer' : '📸 Modifier l\'image'}
                </button>
              </div>

              {/* Module de changement d'image */}
              {showImageUploader && (
                <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Modifier l'image du produit
                  </h3>
                  <LocalImageUploader
                    productId={productId}
                    currentImage={productImage}
                    onImageUpdate={handleImageUpdate}
                  />
                </div>
              )}
            </div>

            {/* Informations produit */}
            <div className="space-y-8">
              
              {/* Titre et prix */}
              <div>
                <h1 className="text-4xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                <div className="flex items-center space-x-4 mb-6">
                  <span className="text-3xl font-bold text-[#C49B86]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price}€
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Disponible
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                {product.description.split('\n').map((line, index) => (
                  <p key={index} className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {line}
                  </p>
                ))}
              </div>

              {/* Détails techniques */}
              <div className="bg-[#FAF9F8] p-6 rounded-lg border border-[#C49B86]">
                <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Détails du produit
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Fabrication :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.fabrication}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Type :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Dimensions :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.dimensions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Délai :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.delai}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Matériaux :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.materials}</span>
                  </div>
                </div>
              </div>

              {/* Sélection quantité et achat */}
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <label className="text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#C49B86] rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                      disabled={quantity <= 1}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="px-4 py-2 text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-lg text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Total : <span className="font-bold text-[#C49B86]">{(product.price * quantity).toFixed(2)}€</span>
                  </span>
                </div>

                {/* Bouton d'achat */}
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-[#F8EDEA] text-[#B97A72] px-8 py-4 rounded-full font-semibold text-lg border-2 border-[#C49B86] hover:bg-[#C49B86] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Ajouter au panier
                </button>

                {/* Phrase poétique */}
                <div className="text-center pt-4">
                  <p className="text-lg text-[#C49B86] italic font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    🌙 <em>Il veille en silence, le cœur plein de douceur...</em>
                  </p>
                </div>
              </div>

              {/* Informations de livraison */}
              <div className="bg-[#EBD2CF] p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <i className="ri-truck-line text-[#B97A72] text-xl"></i>
                  <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Livraison soignée
                  </span>
                </div>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Votre ange sera emballé avec soin et expédié sous 2 à 3 jours ouvrés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Produits similaires */}
      <section className="py-16 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
              Autres créations de Noël
            </h2>
            <p className="text-lg text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Découvrez d'autres anges et créations de la collection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/produit-ange-joyeux-coeur" className="group">
              <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#C49B86] overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/9ca0f2dee5922ad907a452879600467c.png"
                    alt="Ange Joyeux Cœur"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Ange "Joyeux Cœur"
                  </h3>
                  <p className="text-[#C49B86] font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>4€</p>
                </div>
              </div>
            </Link>

            <Link to="/noel" className="group">
              <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#C49B86] overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/fecd7f06c7e4ed4593a2b23090e1f496.png"
                    alt="Ange décoré"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Ange à poser
                  </h3>
                  <p className="text-[#C49B86] font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>20 €</p>
                </div>
              </div>
            </Link>

            <Link to="/noel" className="group">
              <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#C49B86] overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/b25886b6d5302fa4fce5dc966e858d39.png"
                    alt="Lot de 5 anges"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Sapin de Noël
                  </h3>
                  <p className="text-[#C49B86] font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>20 €</p>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/noel"
              className="inline-block bg-[#C49B86] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#B97A72] transition-colors whitespace-nowrap"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Voir toute la collection de Noël
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Section Détails et Informations */}
      <section className="py-16 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Détails de fabrication
              </h2>
              <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p className="flex items-center">
                  <i className="ri-hand-heart-line text-[#C49B86] mr-3"></i>
                  Fait main dans l'atelier Les Carnets de Lolotte
                </p>
                <p className="flex items-center">
                  <i className="ri-book-open-line text-[#C49B86] mr-3"></i>
                  Créé à partir de pages de livres pliées avec soin
                </p>
                <p className="flex items-center">
                  <i className="ri-ruler-line text-[#C49B86] mr-3"></i>
                  Hauteur : environ 8 cm (hors ficelle)
                </p>
                <p className="flex items-center">
                  <i className="ri-time-line text-[#C49B86] mr-3"></i>
                  Délai de fabrication : 2 à 3 jours ouvrés
                </p>
                <p className="flex items-center">
                  <i className="ri-star-line text-[#C49B86] mr-3"></i>
                  Pièce artisanale unique
                </p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Conseils d'entretien
              </h2>
              <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p className="flex items-start">
                  <i className="ri-shield-check-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Éviter l'humidité excessive pour préserver le papier</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-sun-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Protéger de la lumière directe du soleil</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-brush-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Dépoussiérer délicatement avec un pinceau doux</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-heart-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Manipuler avec précaution, c'est une création fragile</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
